import React, { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { useAppContext } from '../../common/ui/app-context';
import FeedbackComponent from '../../common/ui/feedback';
import { onRightPanelFeedbackClicked } from '../../common/utils/events/on-right-panel-feedback-clicked';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../controllers/analytics';
import {
	useApplicationMode,
	useCloudId,
	useExperimentConfig,
	usePrimaryProduct,
	useThirdPartyConfigs,
	useUser,
} from '../../controllers/store/bootstrap';

import { getSearchConfig } from './utils/get-search-config';

const messages = defineMessages({
	feedbackButtonLabel: {
		id: 'search_page.lite_header.feedback_button_label',
		defaultMessage: 'Feedback',
		description: 'Label for the feedback button shown in the lite-mode search page header',
	},
});

/**
 * Renders the "Feedback" button used in the lite-mode search page header.
 *
 * The lite-mode header doesn't render the right rail, so we surface the
 * existing feedback CTA in the header itself. This wrapper resolves all the
 * props the underlying Feedback component needs (cloud id, primary product,
 * user, selected products, etc.) using the same store hooks as the rest of
 * the relay search page so that consumers of `LiteHeader` don't need to plumb
 * this through manually.
 *
 * The button label is overridden from the underlying Feedback component's
 * default ("Give feedback") to a shorter "Feedback" label, since header real
 * estate is constrained.
 */
export const LiteHeaderFeedbackButton = (): React.JSX.Element => {
	const { queryParams } = useAppContext();
	const [cloudId] = useCloudId();
	const [primaryProduct] = usePrimaryProduct();
	const [experimentConfig] = useExperimentConfig();
	const [thirdPartyConfigs] = useThirdPartyConfigs();
	const [user] = useUser();
	const applicationMode = useApplicationMode();
	const intl = useIntl();
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();

	const { selectedProducts } = getSearchConfig({
		cloudId: cloudId || '',
		queryParams,
		thirdPartyConfigs,
	});

	const handleFeedbackClick = useCallback(() => {
		fireAnalyticsEvent(onRightPanelFeedbackClicked({ commonAttributes, nonPrivacySafeAttributes }));
	}, [fireAnalyticsEvent, commonAttributes, nonPrivacySafeAttributes]);

	return (
		<FeedbackComponent
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			experimentConfig={experimentConfig}
			selectedProducts={selectedProducts}
			user={user}
			applicationMode={applicationMode}
			source="search-page-lite-header"
			appearance="subtle"
			buttonLabel={intl.formatMessage(messages.feedbackButtonLabel)}
			onFeedbackClick={handleFeedbackClick}
		/>
	);
};
